import { getLogger } from 'lib/utils/logging';
import AppEvents from 'lib/AppEvents';
import { DataStatus } from 'lib/data/providers/IDataProvider';
import React, { useEffect, useState } from 'react';
import DataService from 'services/data/DataService';
import EventService from 'services/EventService';
import Loader from './app/Loader';

const { log, warn } = getLogger('WFD', { color: 'yellow', enabled: false });

// This component facilitates showing a "loader" skeleton for any given
// component, until the DataService is ready with the given set of data dependencies.

export const makePageContainer = (Component, deps, name = '', skeleton = undefined) => {
    return (props) => {
        return <WaitForData name={name} waitFor={deps} skeleton={skeleton} component={Component} {...props} />;
    };
};

export const makeWaitForData = (Component, deps, name = '', skeleton = undefined) => {
    return (props) => {
        return <WaitForData name={name} waitFor={deps} skeleton={skeleton} component={Component} {...props} />;
    };
};

const WaitForData = (props) => {
    const [loaded, setLoaded] = useState(false);
    const [dataStatus, setDataStatus] = useState('');
    const [dataSources, setDataSources] = useState({});
    const [actualDeps, setActualDeps] = useState(undefined);
    const [error, setError] = useState(null);

    // receives updates from DataProvider
    // Todo: this needs to be broken out per provider, maybe (we rely on the .name property of the event target for now)
    useEffect(() => {
        log('TEST');

        EventService.subscribe(AppEvents.DATA_SOURCE_SAVED, dataReady);
        EventService.subscribe(AppEvents.PROVIDER_PROGRESS_UPDATE, dataUpdate);
        EventService.subscribe(AppEvents.DATA_SOURCE_REMOVED, dataRemoved);

        // this is a main app event after user intitialization, which will recalcualte the cache keys properly.
        // the WaitForData target component should not be shown or do any dependency checking until this happens.
        EventService.subscribe(AppEvents.DATE_KEYS_CHANGED, onDateKeysChanged);

        return () => {
            EventService.unsubscribe(AppEvents.DATA_SOURCE_SAVED, dataReady);
            EventService.unsubscribe(AppEvents.PROVIDER_PROGRESS_UPDATE, dataUpdate);
            EventService.unsubscribe(AppEvents.DATA_SOURCE_REMOVED, dataRemoved);
            EventService.unsubscribe(AppEvents.DATE_KEYS_CHANGED, onDateKeysChanged);
        };
    }, []);

    // this turns the 'waitFor' dependency function into the actual
    // dependency/cache-key names, after the app/timezone has been initialized.
    useEffect(() => {
        if (props.waitFor) {
            let actualDeps = getDependencies();
            setActualDeps(actualDeps);
            updateSources();
        }
    }, [props.waitFor]);

    useEffect(() => {
        if (dataSources && actualDeps) {
            if (hasAllDependencies()) {
                log(props.name, ': has all dependencies:', props.waitFor, '\n', dataSources);
                // we emit this event here so the components themselves have a chance to re-update on subsequent data changes. This could be done better.
                EventService.dispatch(AppEvents.DATA_SOURCES_CHANGED, {
                    name: props.name || '',
                    data: dataSources
                });

                if (!loaded) setLoaded(true);
            } else {
                log(props.name, ': does not have all dependencies:', props.waitFor, ', \nHas only:', dataSources);
                if (loaded) setLoaded(false);
            }
        }
    }, [dataSources]);

    useEffect(() => {
        if (loaded) {
            //console.log("PAGE DATA READY:", props.name, dataSources);
        }
    }, [loaded]);

    const onDateKeysChanged = (event) => {
        //console.log('EVENT: dates recalculated', props.id, event.target, props.waitFor);
        // we don't need to do anything? because the dependencies are now pulled on the fly anyway... but this should be handled more cleanly.
        // if (typeof props.waitFor == 'function') {
        //     console.log('NEW DEPS', props.waitFor, props.waitFor());
        //     setActualDeps(props.waitFor())
        // } else {
        //     setActualDeps(props.waitFor)
        // }
    };

    // converts the dependencies to actual names (the dependencies can be in the form of runtime functions, or array of string names)
    const getDependencies = () => {
        let waitFor = [];
        if (typeof props.waitFor == 'function') {
            waitFor = props.waitFor();
        } else {
            waitFor = props.waitFor;
        }
        return waitFor;
    };

    const dataUpdate = (event) => {
        let waitFor = getDependencies();
        // todo: needs to check dependency tree for DPUs...
        //if (waitFor.includes(event.target.id)) {
        if (event.target.status == DataStatus.PROGRESS) {
            //setDataStatus(event.target);
        }
        //}
    };

    const dataReady = async (event) => {
        // update it this depends on the new data
        let update = false;
        let waitFor = getDependencies();

        if (Array.isArray(event.target)) {
            event.target.forEach((t) => {
                if (waitFor.includes(t.id) && !t.name) {
                    update = true;
                }
            });
        } else if (waitFor.includes(event.target.id)) {
            if (!event.target.name) {
                update = true;
            }
        }

        if (update) {
            await updateSources();
        }
    };

    const dataRemoved = async (event) => {
        let update = false;
        let waitFor = getDependencies();

        if (Array.isArray(event.target)) {
            event.target.forEach((t) => {
                if (waitFor.includes(t)) {
                    update = true;
                }
            });
        } else if (waitFor.includes(event.target)) {
            update = true;
        }

        if (update) {
            await updateSources();
        }
    };

    // pull and stores latest sources
    const updateSources = async () => {
        let waitFor = getDependencies();
        let ds = await DataService.getDataSource(waitFor);
        setDataSources(ds);
    };

    const hasAllDependencies = () => {
        let sources = Object.keys(dataSources);
        let hasAll = true;
        let waitFor = getDependencies();
        waitFor.forEach((w) => {
            if (sources.includes(w)) {
                if (dataSources[w] == undefined) {
                    hasAll = false;
                }
            } else {
                hasAll = false;
            }
        });
        return hasAll;
    };

    let Component = props.component;
    let Skeleton = props.skeleton
        ? props.skeleton
        : (props) => {
            return <Loader loaded={loaded} showText={true} />;
        };

    return !loaded || error ? <Skeleton error={error} dataStatus={dataStatus} /> : <Component {...props} deps={dataSources} />;
};

export default WaitForData;
